/**
 * Authentication hook using Zustand for state and React Query for mutations
 * Manages login, logout, registration, and automatic token refresh
 */

import { create } from 'zustand';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback } from 'react';
import * as authApi from '@/services/authApi';
import type { User, AuthTokens, AuthError, LoginRequest, RegisterRequest } from '@/types/auth';
// google-sign-in/US-001 (T3, AC-001.4, BR-001.2): signOut({global:true}) clears the
// Cognito Hosted-UI session (LogoutURLs, google-sign-in/TS-001) in addition to local
// tokens, so a subsequent "Continue with Google" re-prompts on a shared device.
import { signOut as amplifySignOut } from 'aws-amplify/auth';
// google-sign-in/US-001 (T3, AC-001.3): federated token refresh delegates to Amplify's
// own session refresh (Amplify v6 does not surface a raw Cognito refresh token via
// fetchAuthSession — see oauthReconciler.ts module docstring for the full rationale).
import { getFreshFederatedTokens } from '@/services/oauthReconciler';
// Role-split sign-in (2026-07-29): cleared on explicit logout only — see logout below.
import { forgetRememberedRole } from '@/lib/signInRole';

// BUG-20260626-112424: Inline client-side JWT groups decoder - 2026-06-26
// Decodes cognito:groups from the Cognito idToken payload for UX routing only.
// This is NOT a security boundary — authorization is enforced server-side.
// Mirrors the decode pattern in backend/src/handlers/auth/login.py:71-93 but
// adds base64url char substitution (- -> +, _ -> /) that the backend omits.
// Returns [] on any error (malformed token, missing claim) so students are
// always safely routed to /dashboard (CCM-03 preservation).
function decodeJwtGroups(idToken: string): string[] {
  try {
    const payloadB64Url = idToken.split('.')[1];
    if (!payloadB64Url) return [];
    // base64url -> base64 (Cognito idTokens use base64url encoding)
    let b64 = payloadB64Url.replace(/-/g, '+').replace(/_/g, '/');
    // pad to 4-char boundary
    while (b64.length % 4 !== 0) b64 += '=';
    const payload = JSON.parse(atob(b64)) as Record<string, unknown>;
    const groups = payload['cognito:groups'];
    return Array.isArray(groups) ? (groups as string[]) : [];
  } catch {
    return [];
  }
}

// Zustand store for auth state
interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRefreshing: boolean; // BUG-20260124-181051: Flag to coordinate refresh attempts - 2026-01-24
  error: AuthError | null;
  setUser: (user: User | null) => void;
  setTokens: (tokens: AuthTokens | null) => void;
  setError: (error: AuthError | null) => void;
  setLoading: (loading: boolean) => void;
  setIsRefreshing: (isRefreshing: boolean) => void; // BUG-20260124-181051: Action to set refresh flag - 2026-01-24
  clearAuth: () => void;
}

const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true, // Start true - will be set false after localStorage check
  isRefreshing: false, // BUG-20260124-181051: Initial state for refresh coordination - 2026-01-24
  error: null,
  setUser: (user) => set({ user, isAuthenticated: !!user }),
  setTokens: (tokens) => {
    if (tokens) {
      // Store tokens in localStorage for persistence
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      localStorage.setItem('idToken', tokens.idToken);
      localStorage.setItem('tokenExpiry', String(Date.now() + tokens.expiresIn * 1000));
    }
    set({ tokens });
  },
  setError: (error) => set({ error }),
  setLoading: (isLoading) => set({ isLoading }),
  setIsRefreshing: (isRefreshing) => set({ isRefreshing }), // BUG-20260124-181051: Action for refresh coordination - 2026-01-24
  clearAuth: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('user');
    // BUG-20260124-181051: Reset isRefreshing flag on auth clear - 2026-01-24
    set({ user: null, tokens: null, isAuthenticated: false, error: null, isRefreshing: false });
  },
}));

export function useAuth() {
  const queryClient = useQueryClient();
  const {
    user,
    tokens,
    isAuthenticated,
    isLoading,
    isRefreshing, // BUG-20260124-181051: Destructure refresh flag - 2026-01-24
    error,
    setUser,
    setTokens,
    setError,
    setLoading,
    setIsRefreshing, // BUG-20260124-181051: Destructure refresh action - 2026-01-24
    clearAuth,
  } = useAuthStore();

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (request: LoginRequest) => authApi.login(request),
    onMutate: () => {
      setLoading(true);
      setError(null);
    },
    onSuccess: (data) => {
      // A successful 200 login means the user IS verified
      const verifiedUser = { ...data.user, emailVerified: true };
      setUser(verifiedUser);
      setTokens(data.tokens);
      localStorage.setItem('user', JSON.stringify(verifiedUser));
      setLoading(false);
    },
    onError: (error: AuthError) => {
      setError(error);
      setLoading(false);
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (request: RegisterRequest) => authApi.register(request),
    onMutate: () => {
      setLoading(true);
      setError(null);
    },
    onSuccess: () => {
      setLoading(false);
    },
    onError: (error: AuthError) => {
      setError(error);
      setLoading(false);
    },
  });

  // Refresh token mutation
  // google-sign-in/US-001 (T3, AC-001.3, TO-LL-004): federated sessions
  // (user.channel === 'google') never had a real Cognito refresh token to POST to
  // /auth/refresh (see oauthReconciler.ts module docstring). Branch here — same
  // isRefreshing mutex, same onSuccess/onError handling, same RefreshTokenResponse
  // shape either way, so the auto-refresh useEffect below needs ZERO changes.
  const refreshMutation = useMutation({
    mutationFn: (refreshToken: string) => {
      const isFederated = useAuthStore.getState().user?.channel === 'google';
      return isFederated ? getFreshFederatedTokens() : authApi.refreshToken(refreshToken);
    },
    onSuccess: (data) => {
      const newTokens: AuthTokens = {
        ...tokens!,
        accessToken: data.accessToken,
        idToken: data.idToken,
        expiresIn: data.expiresIn,
      };
      setTokens(newTokens);
      // BUG-20260626-112424: Re-derive groups from the fresh idToken and persist
      // so long-running sessions self-heal and cold boots after refresh are correct.
      // Read current user via getState() (not stale render-closure `user`) to
      // avoid the stale-closure issue noted in TO-LL-004.
      // On the expired-token-boot entry point, call_site_3 (below) has already
      // populated the store user via setUser, so getState().user is non-null here. - 2026-06-26
      const current = useAuthStore.getState().user;
      if (current) {
        const updatedUser = { ...current, groups: decodeJwtGroups(data.idToken) };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
      }
      setIsRefreshing(false); // BUG-20260124-181051: Clear refresh flag on success — keep LAST (TO-LL-004 mutex) - 2026-01-24
    },
    onError: () => {
      // BUG-20260124-181051: Clear refresh flag before clearing auth - 2026-01-24
      setIsRefreshing(false);
      // Refresh failed, clear auth and redirect to login
      clearAuth();
    },
  });

  // Logout function
  // google-sign-in/US-001 (T3, AC-001.4, BR-001.2): now clears the Cognito
  // Hosted-UI session (signOut({global:true}) -> Cognito /logout endpoint,
  // LogoutURLs registered by google-sign-in/TS-001) IN ADDITION to local tokens,
  // so a subsequent "Continue with Google" on a shared device re-prompts fresh
  // authentication instead of silently reusing the previous identity. This is a
  // fail-safe local logout with best-effort remote logout: if the remote
  // Hosted-UI call fails (network etc.), local tokens are STILL cleared — the
  // user is never left half-logged-in. The failure is logged, never swallowed
  // silently (enforces TO-LL-036 fail-safe error handling).
  const logout = useCallback(async () => {
    try {
      await amplifySignOut({ global: true });
    } catch (e) {
      console.warn('Federated Hosted-UI signOut failed, clearing local session anyway', e);
    }
    authApi.logout();
    clearAuth();
    queryClient.clear();
    // Role-split sign-in (2026-07-29): forget the remembered sign-in door. Deliberately
    // here and NOT on session expiry — an expired session is the same person returning,
    // but an explicit sign-out on a shared family device is exactly when the other role
    // needs the picker back. Placed after the fail-safe local clear above so it runs even
    // when the remote Hosted-UI signOut failed.
    forgetRememberedRole();
  }, [clearAuth, queryClient]);

  // google-sign-in/US-001 (T3, AC-001.3): hydrate a completed federated (Google)
  // session into the EXACT same localStorage/Zustand shape email/password login
  // uses. Mirrors loginMutation.onSuccess above verbatim (setUser -> setTokens ->
  // localStorage 'user' write -> setLoading(false)) so AuthGuard/ParentGuard/
  // RootRedirect/api.ts's request interceptor/the refresh timer all work
  // unchanged for federated parents. emailVerified is forced true because a
  // federated Google email is verified by Google itself (AuthGuard's
  // emailVerified===false gate must not bounce a federated parent to
  // /verify-email).
  const hydrateFederatedSession = useCallback(
    (newTokens: AuthTokens, user: User): User => {
      const federatedUser: User = {
        ...user,
        emailVerified: true,
        // Groups come from the idToken's cognito:groups claim (the authoritative
        // source, same as password login), NOT from the /users/me response — the
        // caller must use the RETURNED federatedUser (not its own `user` argument)
        // for any group-dependent routing decision (AC-001.3).
        groups: decodeJwtGroups(newTokens.idToken),
      };
      setUser(federatedUser);
      setTokens(newTokens);
      localStorage.setItem('user', JSON.stringify(federatedUser));
      setLoading(false);
      return federatedUser;
    },
    [setUser, setTokens, setLoading]
  );

  // Auto-refresh token before expiry
  // BUG-20260124-181051: Added isRefreshing guard to prevent concurrent refresh attempts - 2026-01-24
  useEffect(() => {
    if (!tokens || !isAuthenticated) return;

    // Guard against concurrent refresh attempts
    if (refreshMutation.isPending || isRefreshing) return;

    const expiryTime = parseInt(localStorage.getItem('tokenExpiry') || '0');
    const timeUntilExpiry = expiryTime - Date.now();
    const refreshTime = timeUntilExpiry - 5 * 60 * 1000; // Refresh 5 minutes before expiry

    if (refreshTime > 0) {
      const timeoutId = setTimeout(() => {
        // BUG-20260124-181051: Check fresh state before refresh to prevent race condition - 2026-01-24
        if (!refreshMutation.isPending && !useAuthStore.getState().isRefreshing) {
          setIsRefreshing(true);
          refreshMutation.mutate(tokens.refreshToken);
        }
      }, refreshTime);

      return () => clearTimeout(timeoutId);
    } else {
      // Token already expired or about to expire, refresh immediately
      // BUG-20260124-181051: Set flag before immediate refresh - 2026-01-24
      setIsRefreshing(true);
      refreshMutation.mutate(tokens.refreshToken);
    }
  }, [tokens, isAuthenticated, isRefreshing]);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedIdToken = localStorage.getItem('idToken');
    const tokenExpiry = localStorage.getItem('tokenExpiry');

    if (storedUser && storedRefreshToken && storedAccessToken && storedIdToken) {
      const user = JSON.parse(storedUser);
      const expiryTime = parseInt(tokenExpiry || '0');

      if (expiryTime > Date.now()) {
        // Tokens still valid
        // BUG-20260626-112424 call_site_1 (T-2): Backfill groups from the authoritative
        // idToken so pre-2026-06-24 localStorage sessions (which lack a groups field on
        // the persisted user object) are self-healed. Re-persist so subsequent cold boots
        // also read the correct value (TO-LL-005: setLoading(false) stays at line below). - 2026-06-26
        const hydratedUser = { ...user, groups: decodeJwtGroups(storedIdToken) };
        localStorage.setItem('user', JSON.stringify(hydratedUser));
        setUser(hydratedUser);
        setTokens({
          accessToken: storedAccessToken,
          refreshToken: storedRefreshToken,
          idToken: storedIdToken,
          expiresIn: Math.floor((expiryTime - Date.now()) / 1000),
        });
      } else {
        // Tokens expired, try to refresh
        // BUG-20260626-112424 call_site_3 (T-3b): Backfill groups from the stale idToken
        // and call setUser BEFORE triggering refresh. This:
        //   (a) restores isAuthenticated immediately so ParentGuard/AuthGuard do not
        //       redirect to /login during the refresh round-trip (ISSUE-2 fix),
        //   (b) provides a non-null store user so refreshMutation.onSuccess's
        //       `if (current)` guard fires and re-derives groups from the fresh idToken,
        //   (c) preserves emailVerified and all other fields via spread (AuthGuard
        //       verify-email branch safety — ISSUE-6 from SA reviewer).
        // Stale idToken groups are better than none; onSuccess immediately updates them.
        // NOTE: do NOT add an early return here — setLoading(false) below must still run
        // (TO-LL-005). - 2026-06-26
        const hydratedUser = { ...user, groups: decodeJwtGroups(storedIdToken) };
        localStorage.setItem('user', JSON.stringify(hydratedUser));
        setUser(hydratedUser);
        refreshMutation.mutate(storedRefreshToken);
      }
    }
    setLoading(false);
  }, []);

  return {
    user,
    isAuthenticated,
    isLoading: isLoading || loginMutation.isPending || registerMutation.isPending,
    error,
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout,
    // google-sign-in/US-001 (T3): exposed for OAuthCallbackPage (T6) to call after
    // reconcileOAuthSession() resolves.
    hydrateFederatedSession,
    // google-sign-in/US-001 (T6, AC-001.5): local-only session clear (localStorage +
    // Zustand), WITHOUT the Amplify Hosted-UI signOut({global:true}) browser redirect
    // that `logout` performs. OAuthCallbackPage's error paths (cancel/no-email/
    // network) must never trigger a SECOND Hosted-UI redirect while cleaning up a
    // partial/failed session — they only need to guarantee no partial tokens remain.
    clearLocalSession: clearAuth,
  };
}
